import { captureException } from "@sentry/react";
import { useIntl } from "react-intl";

import { ButtonBase } from "@/components/ButtonBase/ButtonBase";
import { IconTraveller } from "@/components/Icons";
import { CaretRightIcon } from "@/components/Icons/rtlReadyIcons";
import { InfoBadge } from "@/features/flights/components/InfoBadge";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import type { TravellerSlots } from "@/features/travellers/contexts/TravellersSelectionContext/TravellersSelectionContext";
import type { FindUserTravelersTravelers } from "@/types/codegen";
import type { TravellerAgeGroup } from "@/types/types";
import {
  SUPPORTED_BAGGAGE_ADDITIONAL_SERVICES_CODES,
  SUPPORTED_MEAL_ADDITIONAL_SERVICES_CODES,
} from "@/utils/constants";
import { getAgeFromDateOfBirth } from "@/utils/getAgefromDateOfBirth";

type Props = {
  travellerSlots: TravellerSlots;
  selectedTravellers: FindUserTravelersTravelers[];
  baggageAllowancePerAdult: number;
  onTravellerClick: (traveller: FindUserTravelersTravelers) => void;
};

export const PassengersAndServicesOverview = ({
  travellerSlots,
  selectedTravellers,
  baggageAllowancePerAdult,
  onTravellerClick,
}: Props) => {
  const intl = useIntl();
  const { getTravellerServices } = useFlightSelectionPayloadContext();

  return (
    <div className="section-card">
      <div className="mb-4 text-lg font-medium text-text-primary">
        {intl.formatMessage({
          defaultMessage: "Passengers & Services",
          description: "Flight checkout passengers and services card title",
        })}
      </div>
      <ul className="-my-3 divide-y divide-stroke-neutral-soft *:py-3">
        {Object.keys(travellerSlots).map((ageGroup) =>
          travellerSlots[ageGroup as TravellerAgeGroup].map(
            (travellerId, idx) => {
              const traveller = selectedTravellers.find(
                (traveller) => traveller.id === travellerId,
              );

              if (!traveller) {
                captureException(
                  new Error("Traveller with given id not found"),
                );
                return null;
              }

              const travellerServices = getTravellerServices(traveller.id);

              const badgesLabels =
                ageGroup === "CHD"
                  ? [
                      intl.formatMessage(
                        {
                          defaultMessage: "Child Age {age}",
                          description: "Age labeled child (e.g. 'Child Age 8')",
                        },
                        {
                          age: getAgeFromDateOfBirth(traveller.birthDate),
                        },
                      ),
                    ]
                  : [
                      intl.formatMessage(
                        {
                          defaultMessage:
                            "{type, select, ADT {Adult} INF {Infant} other {Traveller}} {number}",
                          description:
                            "Numbered traveller slot label (e.g. 'Adult 1', Infant 2')",
                        },
                        {
                          type: ageGroup,
                          number: idx + 1,
                        },
                      ),
                    ];

              const bagsCount =
                baggageAllowancePerAdult +
                travellerServices.reduce(
                  (count, service) =>
                    SUPPORTED_BAGGAGE_ADDITIONAL_SERVICES_CODES.includes(
                      service.categoryCode,
                    )
                      ? count + 1
                      : count,
                  0,
                );
              if (bagsCount > 0) {
                badgesLabels.push(
                  intl.formatMessage(
                    {
                      defaultMessage:
                        "{count, plural, one {# Bag} other {# Bags}}",
                      description: "Number of baggage type services",
                    },
                    { count: bagsCount },
                  ),
                );
              }

              const mealsCount = travellerServices.reduce(
                (count, service) =>
                  SUPPORTED_MEAL_ADDITIONAL_SERVICES_CODES.includes(
                    service.categoryCode,
                  )
                    ? count + 1
                    : count,
                0,
              );
              if (mealsCount > 0) {
                badgesLabels.push(
                  intl.formatMessage(
                    {
                      defaultMessage:
                        "{count, plural, one {# Meal} other {# Meals}}",
                      description: "Number of meal type services",
                    },
                    { count: mealsCount },
                  ),
                );
              }

              return (
                <li key={traveller.id}>
                  <ButtonBase
                    className="flex w-full justify-between"
                    onClick={() => {
                      onTravellerClick(traveller);
                    }}
                  >
                    <div className="space-y-0.5">
                      <div className="flex items-center justify-start gap-1">
                        <IconTraveller color="black" className="size-6" />
                        <div className="text-base font-normal text-text-primary">
                          {`${traveller.firstName} ${traveller.lastName}`}
                        </div>
                      </div>
                      <div className="ms-7 flex gap-2 text-xs font-normal text-text-primary">
                        {badgesLabels.map((badgeLabel) => (
                          <InfoBadge key={badgeLabel} label={badgeLabel} />
                        ))}
                      </div>
                    </div>
                    <CaretRightIcon
                      size={16}
                      color="var(--color-stone)"
                      weight="bold"
                    />
                  </ButtonBase>
                </li>
              );
            },
          ),
        )}
      </ul>
    </div>
  );
};
